import type { ErrorContract } from '@/core/generated/api';
import { VoxErrorCode } from '@/core/generated/api';
import { toast } from 'sonner';
import { handleForbiddenApiError } from '@/core/api/handleForbiddenApiError';
import { getBadRequestMessage, isBadRequestError } from '@/core/api/badRequestError';

/** Проверяет, является ли ответ объектом */
const isRecord = (value: unknown): value is Record<string, unknown> => (
    typeof value === 'object' && value !== null
);

/** Сообщает сотруднику о неуспешном запросе */
export const handleApiError = (
    error: unknown,
    display: 'default' | 'inline' = 'default'
): { message: string; voxErrorCode: VoxErrorCode } => {
    const response = isRecord(error) && isRecord(error.response)
        ? error.response
        : null;

    if (response?.status === 401) {
        if (display === 'default') toast.error('Сессия истекла. Пожалуйста, войдите заново.');

        return {
            message: 'Сессия истекла',
            voxErrorCode: VoxErrorCode.UnspecifiedError
        };
    }

    const errorData = response?.data as ErrorContract | undefined;

    if (response?.status === 403) {
        return {
            message: display === 'inline'
                ? errorData?.message ?? 'Недостаточно прав для выполнения операции'
                : handleForbiddenApiError(error),
            voxErrorCode: errorData?.voxErrorCode || VoxErrorCode.AccessManagementForbidden,
        };
    }

    if (isBadRequestError(error)) {
        return {
            message: getBadRequestMessage(error),
            voxErrorCode: errorData?.voxErrorCode || VoxErrorCode.UnspecifiedError,
        };
    }

    if (errorData) {
        const message = errorData.message || 'Произошла ошибка';

        if (display === 'default') toast.error(message);
        return {
            message: message,
            voxErrorCode: errorData.voxErrorCode || VoxErrorCode.UnspecifiedError
        };
    }

    const fallbackMessage = display === 'default' && isRecord(error) && typeof error.message === 'string'
        ? error.message
        : 'Не удалось выполнить запрос';

    if (display === 'default') toast.error(fallbackMessage);
    return {
        message: fallbackMessage,
        voxErrorCode: VoxErrorCode.UnspecifiedError
    };
};
